import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Search, Users, UserCheck, UserX, Phone, Mail,
  Calendar as CalendarIcon, Briefcase, Wallet, Shield, MapPin,
  TrendingUp, AlertCircle, Cake, Clock, CheckCircle2, Filter,
  X, ArrowUpRight, BadgeCheck, FileText, LogIn, LogOut, Timer, ClipboardList, Repeat
} from 'lucide-react';
import axios from 'axios';
import AttendancePanel from './AttendancePanel';
import { RoutineDefinitions } from './RoutinesTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_PERMISSIONS = [
  { id: 'dashboard', label: 'Ana Sayfa' },
  { id: 'takvim', label: 'Takvim' },
  { id: 'rezervasyonlar', label: 'Rezervasyonlar' },
  { id: 'musteriler', label: 'Müşteriler' },
  { id: 'salonlar', label: 'Salonlar' },
  { id: 'kasa', label: 'Kasa' },
  { id: 'kasa_hesap', label: 'Kasalar: Hesap Ekleme/Silme' },
  { id: 'gunsonu', label: 'Gün Sonu Kontrol / Kapanış' },
  { id: 'hakemler', label: 'Hakemler' },
  { id: 'personel', label: 'Personel' },
  { id: 'analiz', label: 'Analiz' },
  { id: 'demirbas', label: 'Demirbaş' },
  { id: 'geribildirim', label: 'Geri Bildirimler' },
  { id: 'gorevler', label: 'Görevler' },
  { id: 'rutinler', label: 'Rutinler' },
  { id: 'mesai', label: 'Mesai (PDKS)' },
  { id: 'org_view', label: 'Organizasyonlar: Görüntüleme' },
  { id: 'org_create', label: 'Organizasyon: Oluşturma' },
  { id: 'org_edit', label: 'Organizasyon: Düzenleme' },
  { id: 'org_income', label: 'Organizasyon: Gelir Ekleme' },
  { id: 'org_expense', label: 'Organizasyon: Gider Ekleme' },
  { id: 'org_finance_edit', label: 'Organizasyon: Finansal Düzeltme/İade' },
  { id: 'org_complete', label: 'Organizasyon: Tamamlama' },
  { id: 'org_cancel', label: 'Organizasyon: İptal' },
  { id: 'org_report', label: 'Organizasyon: Rapor İndirme' },
  { id: 'islemkayitlari', label: 'İşlem Kayıtları' },
  { id: 'bildirimler', label: 'Bildirimler' }
];

const EMPLOYMENT_TYPES = [
  { id: 'tam_zamanli', label: 'Tam Zamanlı', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Briefcase },
  { id: 'yari_zamanli', label: 'Yarı Zamanlı', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  { id: 'etkinlik_bazli', label: 'Etkinlik Bazlı', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: CalendarIcon }
];

const LEAVE_TYPES = [
  { id: 'yillik', label: 'Yıllık İzin', color: 'bg-blue-100 text-blue-700' },
  { id: 'raporlu', label: 'Raporlu (Hastalık)', color: 'bg-rose-100 text-rose-700' },
  { id: 'ucretsiz', label: 'Ücretsiz İzin', color: 'bg-slate-100 text-slate-700' },
  { id: 'diger', label: 'Diğer', color: 'bg-purple-100 text-purple-700' }
];

const PAYMENT_TYPES = [
  { id: 'maas', label: 'Maaş', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'avans', label: 'Avans', color: 'bg-amber-100 text-amber-700' },
  { id: 'prim', label: 'Prim', color: 'bg-purple-100 text-purple-700' },
  { id: 'diger', label: 'Diğer', color: 'bg-slate-100 text-slate-700' }
];

const PAYMENT_METHODS = [
  { id: 'nakit', label: 'Nakit', icon: '💵' },
  { id: 'garanti_bbva', label: 'Garanti BBVA', icon: '🏦' },
  { id: 'bostanci', label: 'Bostancı', icon: '🏢' }
];

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR'); } catch { return d; }
};
const calculateDays = (start, end) => {
  if (!start || !end) return 0;
  try {
    const s = new Date(start);
    const e = new Date(end);
    return Math.max(Math.floor((e - s) / 86400000) + 1, 0);
  } catch { return 0; }
};
const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const getMeta = (id, list) => list.find(x => x.id === id) || { label: id, color: 'bg-slate-100 text-slate-700' };

const TASK_PRIORITIES = [
  { id: 'dusuk', label: 'Düşük', badge: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400' },
  { id: 'orta', label: 'Orta', badge: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { id: 'yuksek', label: 'Yüksek', badge: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
];
const getTaskPriority = (id) => TASK_PRIORITIES.find(p => p.id === id) || TASK_PRIORITIES[1];

const todayISO = () => new Date().toISOString().split('T')[0];

import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
const PersonnelTab = ({ personnelList = [], fetchPersonnel, isAdmin = false }) => {
  // Filter state
  const [search, setSearch] = useState('');
  const [employmentFilter, setEmploymentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showReimburseModal, setShowReimburseModal] = useState(false);

  // Selected item states
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [editingPerson, setEditingPerson] = useState(null);
  const [reimburseTarget, setReimburseTarget] = useState(null);

  // Detail data
  const [personPayments, setPersonPayments] = useState([]);
  const [personLeaves, setPersonLeaves] = useState([]);
  const [personExpenses, setPersonExpenses] = useState([]);
  const [personAttendance, setPersonAttendance] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [personTasks, setPersonTasks] = useState([]);

  // Forms
  const initNewForm = {
    name: '', phone: '', email: '', position: '', username: '', password: '',
    salary: 0, permissions: [], employment_type: 'tam_zamanli',
    start_date: todayISO(), birth_date: '', address: '',
    annual_leave_days_total: 14, notes: ''
  };
  const [newForm, setNewForm] = useState(initNewForm);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_type: 'maas', payment_method: 'nakit', date: todayISO(), month: '', notes: ''
  });
  const [leaveForm, setLeaveForm] = useState({
    start_date: todayISO(), end_date: todayISO(), leave_type: 'yillik', notes: ''
  });
  const [expenseForm, setExpenseForm] = useState({
    amount: '', description: '', date: todayISO(), notes: ''
  });
  const [reimburseMethod, setReimburseMethod] = useState('nakit');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', priority: 'orta' });
  const [taskFilter, setTaskFilter] = useState('all');
  const [permDraft, setPermDraft] = useState([]);
  const [savingPerms, setSavingPerms] = useState(false);
  const [showBulkTaskModal, setShowBulkTaskModal] = useState(false);
  const [bulkTaskForm, setBulkTaskForm] = useState({ title: '', description: '', due_date: '', priority: 'orta', personnel_ids: [] });

  // ====== Filtered list & stats ======
  const filtered = useMemo(() => {
    let list = personnelList;
    if (employmentFilter !== 'all') list = list.filter(p => (p.employment_type || 'tam_zamanli') === employmentFilter);
    if (statusFilter === 'active') list = list.filter(p => p.active !== false);
    else if (statusFilter === 'inactive') list = list.filter(p => p.active === false);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.position || '').toLowerCase().includes(q) ||
        (p.username || '').toLowerCase().includes(q) ||
        (p.phone || '').includes(q)
      );
    }
    return list;
  }, [personnelList, search, employmentFilter, statusFilter]);

  const stats = useMemo(() => {
    const active = personnelList.filter(p => p.active !== false);
    const fullTime = personnelList.filter(p => (p.employment_type || 'tam_zamanli') === 'tam_zamanli' && p.active !== false);
    const eventBased = personnelList.filter(p => p.employment_type === 'etkinlik_bazli' && p.active !== false);
    const monthlyCost = active.reduce((s, p) => s + (p.salary || 0), 0);
    return {
      total: personnelList.length,
      active: active.length,
      fullTime: fullTime.length,
      eventBased: eventBased.length,
      monthlyCost
    };
  }, [personnelList]);

  // ====== Fetch detail data ======
  const fetchPersonDetails = async (person) => {
    try {
      const [paymentsRes, leavesRes, summaryRes, expensesRes, attendanceRes, tasksRes] = await Promise.all([
        axios.get(`${API}/personnel-payments/${person.id}`),
        axios.get(`${API}/personnel-leaves/${person.id}`),
        axios.get(`${API}/personnel/${person.id}/leave-summary`),
        axios.get(`${API}/personnel-expenses/${person.id}`),
        axios.get(`${API}/attendance/personnel/${person.id}?days=30`),
        axios.get(`${API}/personnel-tasks?personnel_id=${person.id}`)
      ]);
      setPersonPayments(paymentsRes.data || []);
      setPersonLeaves(leavesRes.data || []);
      setLeaveSummary(summaryRes.data || null);
      setPersonExpenses(expensesRes.data || []);
      setPersonAttendance(attendanceRes.data || []);
      setPersonTasks(tasksRes.data?.items || []);
    } catch (e) {
      console.error('Detay yüklenemedi:', e);
    }
  };

  const openDetail = async (person) => {
    setSelectedPerson(person);
    setPermDraft(person.permissions || []);
    await fetchPersonDetails(person);
    setShowDetailModal(true);
  };

  // ====== CRUD: Personnel ======
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newForm.name?.trim() || !newForm.username?.trim() || !newForm.password) {
      toast.error('Ad, kullanıcı adı ve şifre zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/personnel`, newForm);
      toast.success('Personel eklendi');
      setShowAddModal(false);
      setNewForm(initNewForm);
      fetchPersonnel();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Personel eklenemedi');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingPerson?.name?.trim()) { toast.error('Ad zorunludur'); return; }
    try {
      const payload = { ...editingPerson };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/personnel/${editingPerson.id}`, payload);
      toast.success('Personel güncellendi');
      setShowEditModal(false);
      setEditingPerson(null);
      fetchPersonnel();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const handleDelete = async (person) => {
    if (!window.confirm(`"${person.name}" personelini silmek istiyor musunuz?\n\nBu işlem giriş hesabını da silecek.`)) return;
    try {
      await axios.delete(`${API}/personnel/${person.id}`);
      toast.success('Personel silindi');
      fetchPersonnel();
      setShowDetailModal(false);
    } catch {
      toast.error('Silinemedi');
    }
  };

  // ====== CRUD: Payments ======
  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || !selectedPerson) { toast.error('Tutar zorunludur'); return; }
    try {
      await axios.post(`${API}/personnel-payments`, {
        personnel_id: selectedPerson.id,
        amount: parseFloat(paymentForm.amount),
        payment_type: paymentForm.payment_type,
        payment_method: paymentForm.payment_method,
        date: paymentForm.date,
        month: paymentForm.month || null,
        notes: paymentForm.notes
      });
      toast.success('Ödeme kaydedildi (kasaya gider olarak işlendi)');
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_type: 'maas', payment_method: 'nakit', date: todayISO(), month: '', notes: '' });
      await fetchPersonDetails(selectedPerson);
    } catch {
      toast.error('Ödeme kaydedilemedi');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Bu ödemeyi silmek istiyor musunuz?\n\nKasadan da düşülecek.')) return;
    try {
      await axios.delete(`${API}/personnel-payments/${paymentId}`);
      toast.success('Ödeme silindi');
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('Silinemedi'); }
  };

  // ====== CRUD: Leaves ======
  const handleAddLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date || !selectedPerson) {
      toast.error('Başlangıç ve bitiş tarihi zorunludur'); return;
    }
    if (new Date(leaveForm.end_date) < new Date(leaveForm.start_date)) {
      toast.error('Bitiş tarihi başlangıçtan önce olamaz'); return;
    }
    try {
      await axios.post(`${API}/personnel-leaves`, {
        personnel_id: selectedPerson.id,
        start_date: leaveForm.start_date,
        end_date: leaveForm.end_date,
        leave_type: leaveForm.leave_type,
        notes: leaveForm.notes,
        approved: true
      });
      toast.success('İzin kaydedildi');
      setShowLeaveModal(false);
      setLeaveForm({ start_date: todayISO(), end_date: todayISO(), leave_type: 'yillik', notes: '' });
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('İzin kaydedilemedi'); }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Bu izin kaydını silmek istiyor musunuz?')) return;
    try {
      await axios.delete(`${API}/personnel-leaves/${leaveId}`);
      toast.success('İzin silindi');
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('Silinemedi'); }
  };

  // ====== CRUD: Personnel Expenses (Cep harcamaları) ======
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || !expenseForm.description || !selectedPerson) {
      toast.error('Tutar ve açıklama zorunlu'); return;
    }
    try {
      await axios.post(`${API}/personnel-expenses`, {
        personnel_id: selectedPerson.id,
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description,
        date: expenseForm.date,
        notes: expenseForm.notes
      });
      toast.success('Harcama kaydedildi (geri ödeme bekliyor)');
      setShowExpenseModal(false);
      setExpenseForm({ amount: '', description: '', date: todayISO(), notes: '' });
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('Harcama kaydedilemedi'); }
  };

  const openReimburseModal = (expense) => {
    setReimburseTarget(expense);
    setReimburseMethod('nakit');
    setShowReimburseModal(true);
  };

  const handleReimburse = async () => {
    if (!reimburseTarget) return;
    try {
      await axios.put(`${API}/personnel-expenses/${reimburseTarget.id}/reimburse`, {
        payment_method: reimburseMethod
      });
      toast.success(`${formatTL(reimburseTarget.amount)} ödendi (kasadan düşüldü)`);
      setShowReimburseModal(false);
      setReimburseTarget(null);
      await fetchPersonDetails(selectedPerson);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ödeme yapılamadı');
    }
  };

  const handleUnreimburse = async (expense) => {
    if (!window.confirm('Geri ödemeyi iptal etmek istiyor musunuz?\n\nKasadan da düşülecek.')) return;
    try {
      await axios.put(`${API}/personnel-expenses/${expense.id}/unreimburse`);
      toast.success('Geri ödeme iptal edildi');
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleDeleteExpense = async (expense) => {
    if (!window.confirm(`Bu harcamayı silmek istiyor musunuz?\n\n${expense.description} - ${formatTL(expense.amount)}`)) return;
    try {
      await axios.delete(`${API}/personnel-expenses/${expense.id}`);
      toast.success('Harcama silindi');
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('Silinemedi'); }
  };

  // ====== CRUD: Görev Atama ======
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim() || !selectedPerson) { toast.error('Görev başlığı zorunlu'); return; }
    try {
      await axios.post(`${API}/personnel-tasks`, {
        personnel_id: selectedPerson.id,
        title: taskForm.title.trim(),
        description: taskForm.description.trim(),
        due_date: taskForm.due_date || null,
        priority: taskForm.priority,
      });
      toast.success('Görev atandı — personele WhatsApp bildirimi gönderilecek');
      setTaskForm({ title: '', description: '', due_date: '', priority: 'orta' });
      await fetchPersonDetails(selectedPerson);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Görev atanamadı');
    }
  };

  const togglePermDraft = (permId) => {
    setPermDraft(prev => prev.includes(permId) ? prev.filter(x => x !== permId) : [...prev, permId]);
  };
  const savePermissions = async () => {
    if (!selectedPerson) return;
    setSavingPerms(true);
    try {
      await axios.put(`${API}/personnel/${selectedPerson.id}/permissions`, permDraft);
      toast.success('Yetkiler güncellendi');
      setSelectedPerson({ ...selectedPerson, permissions: permDraft });
      if (typeof fetchPersonnel === 'function') fetchPersonnel();
    } catch { toast.error('Yetkiler kaydedilemedi'); }
    finally { setSavingPerms(false); }
  };

  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Bu görevi silmek istiyor musunuz?\n\n${task.title}`)) return;
    try {
      await axios.delete(`${API}/personnel-tasks/${task.id}`);
      toast.success('Görev silindi');
      await fetchPersonDetails(selectedPerson);
    } catch { toast.error('Silinemedi'); }
  };

  const isOverdue = (t) => t.status !== 'done' && t.due_date && new Date(t.due_date) < new Date(todayISO());
  const filteredTasks = useMemo(() => {
    if (taskFilter === 'pending') return personTasks.filter(t => t.status !== 'done');
    if (taskFilter === 'done') return personTasks.filter(t => t.status === 'done');
    if (taskFilter === 'overdue') return personTasks.filter(isOverdue);
    return personTasks;
  }, [personTasks, taskFilter]);

  const toggleBulkPerson = (id) => {
    setBulkTaskForm(f => ({
      ...f,
      personnel_ids: f.personnel_ids.includes(id) ? f.personnel_ids.filter(x => x !== id) : [...f.personnel_ids, id]
    }));
  };

  const handleBulkTask = async (e) => {
    e.preventDefault();
    if (!bulkTaskForm.title.trim()) { toast.error('Görev başlığı zorunlu'); return; }
    if (bulkTaskForm.personnel_ids.length === 0) { toast.error('En az bir personel seçin'); return; }
    try {
      const res = await axios.post(`${API}/personnel-tasks/bulk`, {
        personnel_ids: bulkTaskForm.personnel_ids,
        title: bulkTaskForm.title.trim(),
        description: bulkTaskForm.description.trim(),
        due_date: bulkTaskForm.due_date || null,
        priority: bulkTaskForm.priority,
      });
      toast.success(res.data?.message || 'Toplu görev atandı');
      setShowBulkTaskModal(false);
      setBulkTaskForm({ title: '', description: '', due_date: '', priority: 'orta', personnel_ids: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toplu görev atanamadı');
    }
  };

  // Personel borcu (geri ödenmemiş cep harcamaları)
  const personDebt = useMemo(
    () => personExpenses.filter(e => !e.reimbursed).reduce((s, e) => s + (e.amount || 0), 0),
    [personExpenses]
  );

  const togglePermission = (formSetter, form, permId) => {
    const perms = form.permissions || [];
    const newPerms = perms.includes(permId) ? perms.filter(p => p !== permId) : [...perms, permId];
    formSetter({ ...form, permissions: newPerms });
  };

  const clearFilters = () => { setSearch(''); setEmploymentFilter('all'); setStatusFilter('all'); };
  const hasFilters = search || employmentFilter !== 'all' || statusFilter !== 'all';

  // Selected person'un payment summary'si
  const personPaymentSummary = useMemo(() => {
    const summary = { maas: 0, avans: 0, prim: 0, diger: 0, total: 0 };
    personPayments.forEach(p => {
      const type = p.payment_type || 'diger';
      summary[type] = (summary[type] || 0) + (p.amount || 0);
      summary.total += p.amount || 0;
    });
    return summary;
  }, [personPayments]);

  // Aylık özet: her ay için maaş target, avans, maas, prim, cep harcaması, kalan ödenecek
  const monthlyBreakdown = useMemo(() => {
    const salaryTarget = selectedPerson?.salary || 0;
    const monthMap = {}; // 'YYYY-MM' -> { maas, avans, prim, expenses_pending, expenses_paid, payments: [...], expenses: [...] }

    const ensure = (m) => {
      if (!monthMap[m]) {
        monthMap[m] = {
          month: m,
          maas: 0, avans: 0, prim: 0, diger: 0,
          expenses_pending: 0, expenses_paid: 0,
          payments: [], expenses: [],
        };
      }
      return monthMap[m];
    };

    personPayments.forEach(p => {
      const m = p.month || (p.date ? String(p.date).slice(0, 7) : null);
      if (!m) return;
      const slot = ensure(m);
      const t = p.payment_type || 'diger';
      slot[t] = (slot[t] || 0) + (p.amount || 0);
      slot.payments.push(p);
    });

    personExpenses.forEach(exp => {
      const m = exp.date ? String(exp.date).slice(0, 7) : null;
      if (!m) return;
      const slot = ensure(m);
      if (exp.reimbursed) slot.expenses_paid += (exp.amount || 0);
      else slot.expenses_pending += (exp.amount || 0);
      slot.expenses.push(exp);
    });

    // Mevcut ay her zaman görünsün (boş bile olsa kalan ödenecek görünür)
    const currentMonth = new Date().toISOString().slice(0, 7);
    ensure(currentMonth);

    // Net hesabı: kalan_maaş = salary_target - (maas + avans), + bekleyen_cep_harcaması
    const list = Object.values(monthMap).map(slot => {
      const paid_salary_portion = (slot.maas || 0) + (slot.avans || 0);
      const remaining_salary = Math.max(0, salaryTarget - paid_salary_portion);
      const total_to_pay = remaining_salary + slot.expenses_pending;
      return {
        ...slot,
        salary_target: salaryTarget,
        paid_salary_portion,
        remaining_salary,
        total_to_pay,
        closed: remaining_salary === 0 && slot.expenses_pending === 0,
      };
    });

    // En yeni ay üstte
    list.sort((a, b) => (a.month < b.month ? 1 : -1));
    return list;
  }, [personPayments, personExpenses, selectedPerson]);

  return (
    <div className="space-y-4" data-testid="personnel-tab-root">
      <Tabs defaultValue="list" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2 h-auto" data-testid="personnel-main-tabs">
          <TabsTrigger value="list" className="gap-2 py-2" data-testid="personnel-list-tab">
            <Users className="w-4 h-4" /> Personel Listesi
          </TabsTrigger>
          <TabsTrigger value="attendance" className="gap-2 py-2" data-testid="personnel-attendance-tab">
            <Clock className="w-4 h-4" /> Mesai (PDKS)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-4 mt-0">
      <PageHeader
        icon={Users}
        title="İnsan Kaynakları"
        description="Personel yönetimi, maaş-avans-prim takibi ve yıllık izin"
        testid="personnel-page-header"
        actions={
          <>
            <Button onClick={() => { setBulkTaskForm({ title: '', description: '', due_date: '', personnel_ids: [] }); setShowBulkTaskModal(true); }} variant="outline" className="h-9" data-testid="bulk-task-btn">
              <ClipboardList className="w-4 h-4 mr-1" /> Toplu Görev
            </Button>
            <Button onClick={() => { setNewForm(initNewForm); setShowAddModal(true); }} className="h-9 bg-[#176B91] hover:bg-[#145a7a]" data-testid="add-personnel-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni Personel
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} tone="indigo" testid="kpi-total-personnel" label="Toplam Personel" value={stats.total} sub={`${stats.active} aktif`} />
        <StatCard icon={UserCheck} tone="emerald" testid="kpi-fulltime" label="Tam Zamanlı" value={stats.fullTime} sub="kadrolu personel" />
        <StatCard icon={Clock} tone="amber" testid="kpi-event-based" label="Etkinlik Bazlı" value={stats.eventBased} sub="iş başı çalışan" />
        <StatCard icon={Wallet} tone="rose" testid="kpi-monthly-cost" label="Aylık Maaş Yükü" value={formatTL(stats.monthlyCost)} sub="tam zamanlı toplam" />
      </div>

      {/* ===== Filter Bar ===== */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Ad, pozisyon, telefon ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="personnel-search-input"
              />
            </div>

            <Select value={employmentFilter} onValueChange={setEmploymentFilter}>
              <SelectTrigger className="w-[180px] h-9" data-testid="employment-type-filter">
                <SelectValue placeholder="Çalışma Türü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Çalışma Türleri</SelectItem>
                {EMPLOYMENT_TYPES.map(et => <SelectItem key={et.id} value={et.id}>{et.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] h-9" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="inactive">Pasif</SelectItem>
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-9" data-testid="clear-personnel-filters-btn">
                <X className="w-4 h-4 mr-1" /> Temizle
              </Button>
            )}

            <div className="ml-auto text-sm text-slate-500">
              <span className="font-semibold text-slate-700">{filtered.length}</span> / {personnelList.length} personel
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Personnel Cards Grid ===== */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{personnelList.length === 0 ? 'Henüz personel eklenmemiş' : 'Filtreye uygun personel yok'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="personnel-cards-grid">
          {filtered.map(person => {
            const empMeta = getMeta(person.employment_type || 'tam_zamanli', EMPLOYMENT_TYPES);
            const EmpIcon = empMeta.icon || Briefcase;
            const isActive = person.active !== false;
            return (
              <Card
                key={person.id}
                className={`hover:shadow-xl transition-all cursor-pointer ${!isActive ? 'opacity-60 bg-slate-50' : ''}`}
                onClick={() => openDetail(person)}
                data-testid={`personnel-card-${person.id}`}
              >
                <CardContent className="p-4">
                  {/* Header: Avatar + Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#0B172A] flex items-center justify-center text-white font-bold shadow-md">
                        {initials(person.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{person.name}</p>
                        <p className="text-xs text-slate-500 truncate">{person.position || 'Pozisyon belirtilmemiş'}</p>
                      </div>
                    </div>
                    {isActive ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><BadgeCheck className="w-3 h-3 mr-0.5" /> Aktif</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-600 border-0 text-[10px]">Pasif</Badge>
                    )}
                  </div>

                  {/* Employment Type Badge */}
                  <div className="mb-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${empMeta.color}`}>
                      <EmpIcon className="w-3 h-3" /> {empMeta.label}
                    </span>
                  </div>

                  {/* Info Grid */}
                  <div className="space-y-1.5 text-sm">
                    {person.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-3.5 h-3.5 text-slate-400" />
                        <span className="truncate">{person.phone}</span>
                      </div>
                    )}
                    {person.email && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-3.5 h-3.5 text-slate-400" />
                        <span className="truncate text-xs">{person.email}</span>
                      </div>
                    )}
                    {person.start_date && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs">İşe giriş: {formatDate(person.start_date)}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer: Salary + Username */}
                  <div className="mt-3 pt-3 border-t flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide">Maaş</p>
                      <p className="font-bold text-emerald-600">{formatTL(person.salary || 0)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide">Kullanıcı</p>
                      <p className="font-mono text-xs text-slate-700">@{person.username}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </TabsContent>

        <TabsContent value="attendance" className="mt-0">
          <AttendancePanel personnelList={personnelList} isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>

      {/* ============ MODAL: ADD PERSONNEL ============ */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-personnel-modal">
          <DialogHeader>
            <DialogTitle>Yeni Personel Ekle</DialogTitle>
            <DialogDescription>Sisteme giriş yapabilecek yeni bir personel kaydı oluşturun</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4">
            {/* Genel Bilgiler */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4" /> Kişisel Bilgiler
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ad Soyad *</Label><Input value={newForm.name} onChange={(e) => setNewForm({...newForm, name: e.target.value})} required data-testid="add-name-input" /></div>
                <div><Label>Pozisyon</Label><Input value={newForm.position} onChange={(e) => setNewForm({...newForm, position: e.target.value})} placeholder="Örn: Resepsiyonist" /></div>
                <div><Label>Telefon</Label><Input value={newForm.phone} onChange={(e) => setNewForm({...newForm, phone: e.target.value})} /></div>
                <div><Label>E-posta</Label><Input type="email" value={newForm.email} onChange={(e) => setNewForm({...newForm, email: e.target.value})} /></div>
                <div><Label>İşe Giriş</Label><Input type="date" value={newForm.start_date} onChange={(e) => setNewForm({...newForm, start_date: e.target.value})} /></div>
                <div><Label>Doğum Tarihi</Label><Input type="date" value={newForm.birth_date} onChange={(e) => setNewForm({...newForm, birth_date: e.target.value})} /></div>
              </div>
              <div><Label>Adres</Label><Input value={newForm.address} onChange={(e) => setNewForm({...newForm, address: e.target.value})} /></div>
            </div>

            {/* Çalışma Bilgileri */}
            <div className="bg-emerald-50 rounded-lg p-3 space-y-3">
              <h4 className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Çalışma Bilgileri
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Çalışma Türü</Label>
                  <Select value={newForm.employment_type} onValueChange={(v) => setNewForm({...newForm, employment_type: v})}>
                    <SelectTrigger data-testid="add-employment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT_TYPES.map(et => <SelectItem key={et.id} value={et.id}>{et.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Aylık Maaş (₺)</Label><Input type="number" step="0.01" value={newForm.salary || ''} onChange={(e) => setNewForm({...newForm, salary: parseFloat(e.target.value) || 0})} placeholder="15000" /></div>
                <div><Label>Yıllık İzin (gün)</Label><Input type="number" value={newForm.annual_leave_days_total} onChange={(e) => setNewForm({...newForm, annual_leave_days_total: parseInt(e.target.value) || 0})} /></div>
              </div>
            </div>

            {/* Giriş Bilgileri */}
            <div className="bg-blue-50 rounded-lg p-3 space-y-3">
              <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                <Shield className="w-4 h-4" /> Sistem Erişimi
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Kullanıcı Adı *</Label><Input value={newForm.username} onChange={(e) => setNewForm({...newForm, username: e.target.value})} required data-testid="add-username-input" /></div>
                <div><Label>Şifre *</Label><Input type="password" value={newForm.password} onChange={(e) => setNewForm({...newForm, password: e.target.value})} required data-testid="add-password-input" /></div>
              </div>
              <div>
                <Label className="text-xs">Erişim Yetkileri</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">
                  {ALL_PERMISSIONS.map(perm => {
                    const checked = (newForm.permissions || []).includes(perm.id);
                    return (
                      <button
                        key={perm.id} type="button"
                        onClick={() => togglePermission(setNewForm, newForm, perm.id)}
                        className={`text-xs px-2 py-1.5 rounded border transition-all ${checked ? 'bg-[#0B172A] text-white border-[#0B172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#176B91]'}`}
                      >
                        {checked && '✓ '}{perm.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div><Label>İK Notları</Label><Textarea rows={2} value={newForm.notes} onChange={(e) => setNewForm({...newForm, notes: e.target.value})} placeholder="Ek not..." /></div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="add-personnel-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL: EDIT PERSONNEL ============ */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-personnel-modal">
          <DialogHeader><DialogTitle>Personel Düzenle</DialogTitle></DialogHeader>
          {editingPerson && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Kişisel Bilgiler</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Ad Soyad *</Label><Input value={editingPerson.name || ''} onChange={(e) => setEditingPerson({...editingPerson, name: e.target.value})} required /></div>
                  <div><Label>Pozisyon</Label><Input value={editingPerson.position || ''} onChange={(e) => setEditingPerson({...editingPerson, position: e.target.value})} /></div>
                  <div><Label>Telefon</Label><Input value={editingPerson.phone || ''} onChange={(e) => setEditingPerson({...editingPerson, phone: e.target.value})} /></div>
                  <div><Label>E-posta</Label><Input type="email" value={editingPerson.email || ''} onChange={(e) => setEditingPerson({...editingPerson, email: e.target.value})} /></div>
                  <div><Label>İşe Giriş</Label><Input type="date" value={editingPerson.start_date || ''} onChange={(e) => setEditingPerson({...editingPerson, start_date: e.target.value})} /></div>
                  <div><Label>Doğum Tarihi</Label><Input type="date" value={editingPerson.birth_date || ''} onChange={(e) => setEditingPerson({...editingPerson, birth_date: e.target.value})} /></div>
                </div>
                <div><Label>Adres</Label><Input value={editingPerson.address || ''} onChange={(e) => setEditingPerson({...editingPerson, address: e.target.value})} /></div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-3 space-y-3">
                <h4 className="text-sm font-semibold text-emerald-800">Çalışma Bilgileri</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Çalışma Türü</Label>
                    <Select value={editingPerson.employment_type || 'tam_zamanli'} onValueChange={(v) => setEditingPerson({...editingPerson, employment_type: v})}>
                      <SelectTrigger data-testid="edit-employment-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{EMPLOYMENT_TYPES.map(et => <SelectItem key={et.id} value={et.id}>{et.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Aylık Maaş</Label><Input type="number" step="0.01" value={editingPerson.salary || ''} onChange={(e) => setEditingPerson({...editingPerson, salary: parseFloat(e.target.value) || 0})} /></div>
                  <div><Label>Yıllık İzin</Label><Input type="number" value={editingPerson.annual_leave_days_total ?? 14} onChange={(e) => setEditingPerson({...editingPerson, annual_leave_days_total: parseInt(e.target.value) || 0})} /></div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 space-y-3">
                <h4 className="text-sm font-semibold text-blue-800">Sistem Erişimi</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Kullanıcı Adı</Label><Input value={editingPerson.username || ''} onChange={(e) => setEditingPerson({...editingPerson, username: e.target.value})} /></div>
                  <div><Label>Yeni Şifre</Label><Input type="password" value={editingPerson.password || ''} onChange={(e) => setEditingPerson({...editingPerson, password: e.target.value})} placeholder="Boş bırakırsanız değişmez" /></div>
                </div>
                <div>
                  <Label className="text-xs">Erişim Yetkileri</Label>
                  <div className="grid grid-cols-3 gap-1.5 mt-1">
                    {ALL_PERMISSIONS.map(perm => {
                      const checked = (editingPerson.permissions || []).includes(perm.id);
                      return (
                        <button
                          key={perm.id} type="button"
                          onClick={() => togglePermission(setEditingPerson, editingPerson, perm.id)}
                          className={`text-xs px-2 py-1.5 rounded border transition-all ${checked ? 'bg-[#0B172A] text-white border-[#0B172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#176B91]'}`}
                        >
                          {checked && '✓ '}{perm.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <input type="checkbox" id="active-edit" checked={editingPerson.active !== false} onChange={(e) => setEditingPerson({...editingPerson, active: e.target.checked})} className="rounded" />
                  <Label htmlFor="active-edit" className="cursor-pointer text-sm">Aktif (sisteme giriş yapabilir)</Label>
                </div>
              </div>

              <div><Label>İK Notları</Label><Textarea rows={2} value={editingPerson.notes || ''} onChange={(e) => setEditingPerson({...editingPerson, notes: e.target.value})} /></div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">İptal</Button>
                <Button type="submit" className="flex-1 bg-[#0B172A] hover:bg-[#13233C]" data-testid="edit-personnel-submit-btn">Güncelle</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ MODAL: DETAIL ============ */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="personnel-detail-modal">
          {selectedPerson && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-[#0B172A] flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {initials(selectedPerson.name)}
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-xl">{selectedPerson.name}</DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-0.5">
                      <span>{selectedPerson.position || 'Pozisyon yok'}</span>
                      <span className="text-slate-300">•</span>
                      <span className="font-mono text-xs">@{selectedPerson.username}</span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Tabs defaultValue="info" data-testid="detail-subtabs">
                <TabsList className="grid w-full grid-cols-7 h-auto">
                  <TabsTrigger value="info" className="gap-1 py-2" data-testid="detail-tab-info"><Users className="w-4 h-4" /> Bilgiler</TabsTrigger>
                  <TabsTrigger value="payments" className="gap-1 py-2" data-testid="detail-tab-payments"><Wallet className="w-4 h-4" /> Ödemeler ({personPayments.length})</TabsTrigger>
                  <TabsTrigger value="expenses" className="gap-1 py-2 relative" data-testid="detail-tab-expenses">
                    <FileText className="w-4 h-4" /> Harcamalar
                    {personDebt > 0 && <Badge className="absolute -top-1 -right-1 h-4 px-1 text-[9px] bg-rose-500 hover:bg-rose-500">{formatTL(personDebt).replace('₺', '')}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="gap-1 py-2 relative" data-testid="detail-tab-tasks">
                    <ClipboardList className="w-4 h-4" /> Görevler
                    {personTasks.filter(t => t.status !== 'done').length > 0 && <Badge className="absolute -top-1 -right-1 h-4 px-1 text-[9px] bg-indigo-500 hover:bg-indigo-500">{personTasks.filter(t => t.status !== 'done').length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="routines" className="gap-1 py-2" data-testid="detail-tab-routines"><Repeat className="w-4 h-4" /> Rutinler</TabsTrigger>
                  <TabsTrigger value="attendance" className="gap-1 py-2" data-testid="detail-tab-attendance"><Clock className="w-4 h-4" /> Mesai ({personAttendance.length})</TabsTrigger>
                  <TabsTrigger value="leaves" className="gap-1 py-2" data-testid="detail-tab-leaves"><CalendarIcon className="w-4 h-4" /> İzinler ({personLeaves.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="routines" className="mt-3">
                  <RoutineDefinitions personnelId={selectedPerson.id} personnelName={selectedPerson.name} />
                </TabsContent>


                {/* === Tab: Bilgiler === */}
                <TabsContent value="info" className="space-y-3 mt-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <Card><CardContent className="p-3 space-y-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Kişisel</p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> <span>{selectedPerson.phone || '-'}</span></div>
                        <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> <span>{selectedPerson.email || '-'}</span></div>
                        <div className="flex items-center gap-2"><Cake className="w-4 h-4 text-slate-400" /> <span>{formatDate(selectedPerson.birth_date)}</span></div>
                        <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-slate-400 mt-0.5" /> <span>{selectedPerson.address || '-'}</span></div>
                      </div>
                    </CardContent></Card>

                    <Card><CardContent className="p-3 space-y-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Çalışma</p>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-400" /><span>{getMeta(selectedPerson.employment_type || 'tam_zamanli', EMPLOYMENT_TYPES).label}</span></div>
                        <div className="flex items-center gap-2"><CalendarIcon className="w-4 h-4 text-slate-400" /><span>İşe giriş: {formatDate(selectedPerson.start_date)}</span></div>
                        <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-slate-400" /><span className="font-bold text-emerald-600">{formatTL(selectedPerson.salary)} / ay</span></div>
                        <div className="flex items-center gap-2">
                          {selectedPerson.active !== false ? (
                            <><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-emerald-700 font-medium">Aktif</span></>
                          ) : (
                            <><UserX className="w-4 h-4 text-slate-400" /><span className="text-slate-500">Pasif</span></>
                          )}
                        </div>
                      </div>
                    </CardContent></Card>
                  </div>

                  {/* Yetkiler — Görebileceği Sayfalar (düzenlenebilir) */}
                  <Card><CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Görebileceği Sayfalar</p>
                      <button
                        type="button"
                        className="text-[11px] text-indigo-600 hover:underline"
                        onClick={() => setPermDraft(permDraft.length === ALL_PERMISSIONS.length ? [] : ALL_PERMISSIONS.map(p => p.id))}
                        data-testid="perm-select-all"
                      >
                        Tümünü Seç / Kaldır
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5" data-testid="perm-checklist">
                      {ALL_PERMISSIONS.map(perm => {
                        const checked = permDraft.includes(perm.id);
                        return (
                          <button
                            key={perm.id} type="button" onClick={() => togglePermDraft(perm.id)}
                            className={`flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded border transition-all ${checked ? 'bg-indigo-50 border-indigo-400' : 'bg-white border-slate-200 hover:border-indigo-200'}`}
                            data-testid={`perm-${perm.id}`}
                          >
                            <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${checked ? 'bg-indigo-600 text-white' : 'border border-slate-300'}`}>{checked && '✓'}</span>
                            <span className="truncate">{perm.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      onClick={savePermissions} disabled={savingPerms}
                      className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700" size="sm"
                      data-testid="perm-save-btn"
                    >
                      {savingPerms ? 'Kaydediliyor...' : 'Yetkileri Kaydet'}
                    </Button>
                  </CardContent></Card>

                  {selectedPerson.notes && (
                    <Card><CardContent className="p-3">
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">İK Notları</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedPerson.notes}</p>
                    </CardContent></Card>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => { setEditingPerson({...selectedPerson, password: ''}); setShowDetailModal(false); setShowEditModal(true); }} className="flex-1" data-testid="detail-edit-btn">
                      <Edit className="w-4 h-4 mr-1" /> Düzenle
                    </Button>
                    <Button variant="destructive" onClick={() => handleDelete(selectedPerson)} className="flex-1" data-testid="detail-delete-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Sil
                    </Button>
                  </div>
                </TabsContent>

                {/* === Tab: Ödemeler === */}
                <TabsContent value="payments" className="space-y-3 mt-3">
                  {/* Özet kartları */}
                  <div className="grid grid-cols-4 gap-2">
                    <Card><CardContent className="p-2 text-center">
                      <p className="text-[10px] text-slate-500 uppercase">Maaş</p>
                      <p className="text-sm font-bold text-emerald-700">{formatTL(personPaymentSummary.maas)}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-2 text-center">
                      <p className="text-[10px] text-slate-500 uppercase">Avans</p>
                      <p className="text-sm font-bold text-amber-700">{formatTL(personPaymentSummary.avans)}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-2 text-center">
                      <p className="text-[10px] text-slate-500 uppercase">Prim</p>
                      <p className="text-sm font-bold text-purple-700">{formatTL(personPaymentSummary.prim)}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-2 text-center">
                      <p className="text-[10px] text-slate-500 uppercase">Toplam</p>
                      <p className="text-sm font-bold text-slate-800">{formatTL(personPaymentSummary.total)}</p>
                    </CardContent></Card>
                  </div>

                  <Button onClick={() => setShowPaymentModal(true)} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="add-payment-btn">
                    <Plus className="w-4 h-4 mr-1" /> Ödeme Ekle (Maaş/Avans/Prim)
                  </Button>

                  {/* === AYLIK ÖZET — yeni === */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 inline-flex items-center gap-1.5">
                        <CalendarIcon className="w-3.5 h-3.5 text-indigo-600" /> Aylık Özet
                      </h4>
                      <p className="text-[10px] text-slate-400">Aylık maaş: <strong>{formatTL(selectedPerson.salary || 0)}</strong></p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid="monthly-breakdown">
                      {monthlyBreakdown.map(m => {
                        const monthLabel = (() => {
                          const [y, mo] = m.month.split('-');
                          const date = new Date(parseInt(y), parseInt(mo) - 1, 1);
                          return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
                        })();
                        const isClosed = m.closed;
                        return (
                          <div
                            key={m.month}
                            className={`rounded-lg border ${isClosed ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'} p-3`}
                            data-testid={`month-card-${m.month}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-sm text-slate-800 capitalize">{monthLabel}</p>
                                {isClosed ? (
                                  <Badge className="bg-emerald-600 text-white border-0 text-[9px] h-4">Kapandı</Badge>
                                ) : (
                                  <Badge className="bg-rose-600 text-white border-0 text-[9px] h-4">Açık</Badge>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Ödenecek</p>
                                <p className={`text-lg font-black tabular-nums ${isClosed ? 'text-emerald-700' : 'text-rose-700'}`}>
                                  {formatTL(m.total_to_pay)}
                                </p>
                              </div>
                            </div>

                            <div className="space-y-1 text-xs">
                              <div className="flex items-center justify-between text-slate-600">
                                <span>Aylık maaş hedefi</span>
                                <span className="font-semibold tabular-nums">{formatTL(m.salary_target)}</span>
                              </div>
                              {m.maas > 0 && (
                                <div className="flex items-center justify-between text-emerald-700">
                                  <span>− Ödenen maaş</span>
                                  <span className="font-semibold tabular-nums">{formatTL(m.maas)}</span>
                                </div>
                              )}
                              {m.avans > 0 && (
                                <div className="flex items-center justify-between text-amber-700">
                                  <span>− Çekilen avans</span>
                                  <span className="font-semibold tabular-nums">{formatTL(m.avans)}</span>
                                </div>
                              )}
                              <div className="flex items-center justify-between border-t border-slate-200 pt-1 text-slate-700">
                                <span>= Kalan maaş borcu</span>
                                <span className="font-bold tabular-nums">{formatTL(m.remaining_salary)}</span>
                              </div>
                              {m.expenses_pending > 0 && (
                                <div className="flex items-center justify-between text-rose-700 bg-rose-100/50 rounded px-1 py-0.5">
                                  <span>+ Cep harcaması (geri ödenecek)</span>
                                  <span className="font-semibold tabular-nums">{formatTL(m.expenses_pending)}</span>
                                </div>
                              )}
                              {m.expenses_paid > 0 && (
                                <div className="flex items-center justify-between text-emerald-600 text-[10px]">
                                  <span>✓ Bu ay geri ödenen harcama</span>
                                  <span className="tabular-nums">{formatTL(m.expenses_paid)}</span>
                                </div>
                              )}
                              {m.prim > 0 && (
                                <div className="flex items-center justify-between text-purple-700 text-[10px]">
                                  <span>+ Verilen prim (ekstra)</span>
                                  <span className="tabular-nums">{formatTL(m.prim)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Detaylı işlem listesi */}
                  <div className="space-y-1.5">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 inline-flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-slate-500" /> Tüm İşlemler
                    </h4>
                  <Card><CardContent className="p-0">
                    {personPayments.length === 0 ? (
                      <p className="text-center py-8 text-slate-400 text-sm">Henüz ödeme kaydı yok</p>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium text-slate-600">Tarih</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Dönem</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Tür</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Kasa</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Açıklama</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Tutar</th>
                              <th className="px-2 py-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {personPayments.map(p => {
                              const meta = getMeta(p.payment_type, PAYMENT_TYPES);
                              const pmMeta = PAYMENT_METHODS.find(x => x.id === (p.payment_method || 'nakit')) || PAYMENT_METHODS[0];
                              return (
                                <tr key={p.id} className="border-b hover:bg-slate-50">
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(p.date)}</td>
                                  <td className="px-2 py-2 text-xs text-slate-500 whitespace-nowrap">{p.month || '-'}</td>
                                  <td className="px-2 py-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${meta.color}`}>{meta.label}</span></td>
                                  <td className="px-2 py-2 text-xs"><span>{pmMeta.icon}</span> {pmMeta.label}</td>
                                  <td className="px-2 py-2 text-slate-600 text-xs truncate max-w-[140px]">{p.notes || '-'}</td>
                                  <td className="px-2 py-2 text-right font-semibold">{formatTL(p.amount)}</td>
                                  <td className="px-2 py-2">
                                    <Button size="sm" variant="ghost" onClick={() => handleDeletePayment(p.id)} className="h-6 w-6 p-0">
                                      <Trash2 className="w-3 h-3 text-rose-500" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent></Card>
                  </div>
                </TabsContent>

                {/* === Tab: Harcamalar (Cep Harcaması) === */}
                <TabsContent value="expenses" className="space-y-3 mt-3">
                  {personDebt > 0 && (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-rose-700 font-medium uppercase tracking-wide">Geri Ödenecek Toplam</p>
                          <p className="text-2xl font-bold text-rose-900">{formatTL(personDebt)}</p>
                        </div>
                        <FileText className="w-8 h-8 text-rose-300" />
                      </div>
                    </div>
                  )}

                  <Button onClick={() => setShowExpenseModal(true)} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="add-expense-btn">
                    <Plus className="w-4 h-4 mr-1" /> Yeni Harcama Ekle
                  </Button>

                  <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                    💡 <strong>Bilgi:</strong> Personel kendi cebinden harcadığı tutarlar buraya yazılır. "Geri Öde" tıklandığında seçilen kasadan gider olarak işlenir.
                  </div>

                  <Card><CardContent className="p-0">
                    {personExpenses.length === 0 ? (
                      <p className="text-center py-8 text-slate-400 text-sm">Henüz harcama kaydı yok</p>
                    ) : (
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium text-slate-600">Tarih</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Açıklama</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Tutar</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center">Durum</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center w-[120px]">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {personExpenses.map(exp => {
                              const pmMeta = PAYMENT_METHODS.find(x => x.id === exp.reimbursed_payment_method) || null;
                              return (
                                <tr key={exp.id} className={`border-b hover:bg-slate-50 ${exp.reimbursed ? 'opacity-70' : ''}`} data-testid={`expense-row-${exp.id}`}>
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(exp.date)}</td>
                                  <td className="px-2 py-2">
                                    <div className="font-medium text-slate-800 truncate max-w-[180px]">{exp.description}</div>
                                    {exp.notes && <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{exp.notes}</div>}
                                  </td>
                                  <td className="px-2 py-2 text-right font-semibold">{formatTL(exp.amount)}</td>
                                  <td className="px-2 py-2 text-center">
                                    {exp.reimbursed ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                                        <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Ödendi {pmMeta && `• ${pmMeta.icon}`}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]">Bekliyor</Badge>
                                    )}
                                  </td>
                                  <td className="px-2 py-2">
                                    <div className="flex items-center justify-center gap-1">
                                      {exp.reimbursed ? (
                                        <Button size="sm" variant="outline" onClick={() => handleUnreimburse(exp)} className="h-6 text-[10px] px-2 border-rose-200 text-rose-600" data-testid={`unreimburse-${exp.id}`}>
                                          Geri Al
                                        </Button>
                                      ) : (
                                        <Button size="sm" onClick={() => openReimburseModal(exp)} className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700" data-testid={`reimburse-${exp.id}`}>
                                          <CheckCircle2 className="w-3 h-3 mr-0.5" /> Geri Öde
                                        </Button>
                                      )}
                                      <Button size="sm" variant="ghost" onClick={() => handleDeleteExpense(exp)} className="h-6 w-6 p-0">
                                        <Trash2 className="w-3 h-3 text-rose-500" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent></Card>
                </TabsContent>

                {/* === Tab: Mesai === */}
                <TabsContent value="attendance" className="space-y-3 mt-3">
                  {(() => {
                    const totalMin = personAttendance.reduce((s, r) => s + (r.duration_minutes || 0), 0);
                    const totalH = Math.floor(totalMin / 60);
                    const totalM = totalMin % 60;
                    const completed = personAttendance.filter(r => r.check_out_time).length;
                    const open = personAttendance.filter(r => !r.check_out_time).length;
                    const uniqueDays = new Set(personAttendance.map(r => r.date)).size;
                    return (
                      <Card className="bg-[#F8FAFC] border-[#E2E8F0]">
                        <CardContent className="p-3">
                          <p className="text-sm font-semibold text-cyan-900 mb-2">Son 30 Gün Özeti</p>
                          <div className="grid grid-cols-4 gap-2 text-center">
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase">Toplam Mesai</p>
                              <p className="text-sm font-bold text-blue-700">{totalH}sa {totalM}dk</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase">Çalışılan Gün</p>
                              <p className="text-sm font-bold text-emerald-700">{uniqueDays}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase">Tamamlanan</p>
                              <p className="text-sm font-bold text-slate-700">{completed}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase">Açık</p>
                              <p className="text-sm font-bold text-amber-700">{open}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  <Card><CardContent className="p-0">
                    {personAttendance.length === 0 ? (
                      <p className="text-center py-8 text-slate-400 text-sm">Son 30 günde mesai kaydı yok</p>
                    ) : (
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium text-slate-600">Tarih</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Giriş</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Çıkış</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Süre</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center">Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {personAttendance.map(r => {
                              const dur = r.duration_minutes;
                              const fmtDur = dur != null ? `${Math.floor(dur / 60)}sa ${dur % 60}dk` : '-';
                              const fmtTime = (iso) => {
                                if (!iso) return '-';
                                try { return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
                              };
                              return (
                                <tr key={r.id} className="border-b hover:bg-slate-50">
                                  <td className="px-3 py-2 whitespace-nowrap font-medium">{formatDate(r.date)}</td>
                                  <td className="px-2 py-2 font-mono text-xs text-emerald-700">
                                    <LogIn className="w-3 h-3 inline mr-0.5" />{fmtTime(r.check_in_time)}
                                  </td>
                                  <td className="px-2 py-2 font-mono text-xs text-slate-600">
                                    {r.check_out_time ? (<><LogOut className="w-3 h-3 inline mr-0.5" />{fmtTime(r.check_out_time)}</>) : <span className="text-amber-600">- (açık)</span>}
                                  </td>
                                  <td className="px-2 py-2 text-right font-semibold text-blue-700">{fmtDur}</td>
                                  <td className="px-2 py-2 text-center">
                                    {r.check_out_time ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Tamamlandı</Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Açık</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent></Card>
                </TabsContent>

                {/* === Tab: İzinler === */}
                <TabsContent value="leaves" className="space-y-3 mt-3">
                  {/* Yıllık izin özeti */}
                  {leaveSummary && (
                    <Card className="bg-[#F8FAFC] border-[#E2E8F0]">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold text-blue-900">Yıllık İzin ({leaveSummary.year})</p>
                          <p className="text-xs text-blue-700">
                            <span className="font-bold text-lg">{leaveSummary.annual_remaining}</span> / {leaveSummary.annual_total} gün kaldı
                          </p>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
                          <div
                            className="bg-[#176B91] h-full transition-all"
                            style={{ width: `${leaveSummary.annual_total > 0 ? (leaveSummary.annual_used / leaveSummary.annual_total) * 100 : 0}%` }}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase">Kullanılan</p>
                            <p className="text-sm font-bold text-blue-700">{leaveSummary.annual_used} gün</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase">Raporlu</p>
                            <p className="text-sm font-bold text-rose-700">{leaveSummary.sick_used} gün</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase">Ücretsiz</p>
                            <p className="text-sm font-bold text-slate-700">{leaveSummary.unpaid_used} gün</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Button onClick={() => setShowLeaveModal(true)} className="w-full bg-[#0B172A] hover:bg-[#13233C]" data-testid="add-leave-btn">
                    <Plus className="w-4 h-4 mr-1" /> Yeni İzin Tanımla
                  </Button>

                  <Card><CardContent className="p-0">
                    {personLeaves.length === 0 ? (
                      <p className="text-center py-8 text-slate-400 text-sm">Henüz izin kaydı yok</p>
                    ) : (
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium text-slate-600">Başlangıç</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Bitiş</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center">Gün</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Tür</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Açıklama</th>
                              <th className="px-2 py-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {personLeaves.map(l => {
                              const meta = getMeta(l.leave_type, LEAVE_TYPES);
                              return (
                                <tr key={l.id} className="border-b hover:bg-slate-50">
                                  <td className="px-3 py-2 whitespace-nowrap">{formatDate(l.start_date)}</td>
                                  <td className="px-2 py-2 whitespace-nowrap">{formatDate(l.end_date)}</td>
                                  <td className="px-2 py-2 text-center font-bold">{l.days}</td>
                                  <td className="px-2 py-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium ${meta.color}`}>{meta.label}</span></td>
                                  <td className="px-2 py-2 text-slate-600 text-xs truncate max-w-[150px]">{l.notes || '-'}</td>
                                  <td className="px-2 py-2">
                                    <Button size="sm" variant="ghost" onClick={() => handleDeleteLeave(l.id)} className="h-6 w-6 p-0">
                                      <Trash2 className="w-3 h-3 text-rose-500" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent></Card>
                </TabsContent>

                {/* === Tab: Görevler === */}
                <TabsContent value="tasks" className="space-y-3 mt-3">
                  <form onSubmit={handleAddTask} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-3" data-testid="assign-task-form">
                    <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" /> Yeni Görev Ata
                    </h4>
                    <div>
                      <Label className="text-xs">Görev Başlığı *</Label>
                      <Input
                        value={taskForm.title}
                        onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                        placeholder="Örn: Beşiktaş bölgesindeki salonların araştırmasını yap"
                        data-testid="task-title-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Açıklama</Label>
                      <Textarea
                        rows={2}
                        value={taskForm.description}
                        onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                        placeholder="Görev detayları..."
                        data-testid="task-description-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Son Tarih</Label>
                      <Input
                        type="date"
                        value={taskForm.due_date}
                        onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                        data-testid="task-due-date-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Öncelik</Label>
                      <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                        <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TASK_PRIORITIES.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              <span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} /> {p.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="assign-task-btn">
                      <Plus className="w-4 h-4 mr-1" /> Görev Ata
                    </Button>
                    <p className="text-[11px] text-indigo-700 flex items-center gap-1">
                      💬 Görev atanınca personele WhatsApp bildirimi gönderilir (telefonu varsa).
                    </p>
                  </form>

                  <Card><CardContent className="p-0">
                    {personTasks.length === 0 ? (
                      <p className="text-center py-8 text-slate-400 text-sm">Henüz görev atanmamış</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-1.5 p-2 border-b bg-slate-50" data-testid="task-filters">
                          {[
                            { id: 'all', label: `Tümü (${personTasks.length})` },
                            { id: 'pending', label: `Bekleyen (${personTasks.filter(t => t.status !== 'done').length})` },
                            { id: 'done', label: `Tamamlanan (${personTasks.filter(t => t.status === 'done').length})` },
                            { id: 'overdue', label: `Gecikmiş (${personTasks.filter(isOverdue).length})` },
                          ].map(f => (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => setTaskFilter(f.id)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-all ${taskFilter === f.id ? (f.id === 'overdue' ? 'bg-rose-600 text-white border-rose-600' : 'bg-indigo-600 text-white border-indigo-600') : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}
                              data-testid={`task-filter-${f.id}`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                        {filteredTasks.length === 0 ? (
                          <p className="text-center py-8 text-slate-400 text-sm">Bu filtreye uygun görev yok</p>
                        ) : (
                          <div className="max-h-72 overflow-y-auto divide-y" data-testid="task-list">
                            {filteredTasks.map(t => {
                              const overdue = isOverdue(t);
                              return (
                          <div key={t.id} className={`flex items-start justify-between gap-2 p-3 ${t.status === 'done' ? 'bg-emerald-50/50 opacity-75' : overdue ? 'bg-rose-50/50' : ''}`} data-testid={`admin-task-${t.id}`}>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className={`font-medium text-sm text-slate-800 ${t.status === 'done' ? 'line-through' : ''}`}>{t.title}</p>
                                <Badge className={`border text-[10px] ${getTaskPriority(t.priority).badge}`}>{getTaskPriority(t.priority).label}</Badge>
                                {t.status === 'done' ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Tamamlandı</Badge>
                                ) : overdue ? (
                                  <Badge className="bg-rose-100 text-rose-700 border-0 text-[10px]"><AlertCircle className="w-2.5 h-2.5 mr-0.5" /> Gecikmiş</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Bekliyor</Badge>
                                )}
                              </div>
                              {t.description && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{t.description}</p>}
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-400">
                                <span>Atandı: {formatDate(t.created_at)}</span>
                                {t.due_date && <span className={overdue ? 'text-rose-500 font-medium' : 'text-rose-500'}>Son tarih: {formatDate(t.due_date)}</span>}
                                {t.completed_at && <span className="text-emerald-500">Bitti: {formatDate(t.completed_at)}</span>}
                              </div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteTask(t)} className="h-7 w-7 p-0 shrink-0" data-testid={`delete-task-${t.id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                            </Button>
                          </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </CardContent></Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ MODAL: BULK TASK ============ */}
      <Dialog open={showBulkTaskModal} onOpenChange={setShowBulkTaskModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-task-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-indigo-600" /> Toplu Görev Ata</DialogTitle>
            <DialogDescription>Aynı görevi birden fazla personele tek seferde atayın</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkTask} className="space-y-4">
            <div>
              <Label>Görev Başlığı *</Label>
              <Input value={bulkTaskForm.title} onChange={(e) => setBulkTaskForm({ ...bulkTaskForm, title: e.target.value })} placeholder="Örn: Salon araştırması yap" data-testid="bulk-task-title-input" />
            </div>
            <div>
              <Label>Açıklama</Label>
              <Textarea rows={2} value={bulkTaskForm.description} onChange={(e) => setBulkTaskForm({ ...bulkTaskForm, description: e.target.value })} placeholder="Görev detayları..." data-testid="bulk-task-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Son Tarih</Label>
                <Input type="date" value={bulkTaskForm.due_date} onChange={(e) => setBulkTaskForm({ ...bulkTaskForm, due_date: e.target.value })} data-testid="bulk-task-due-date-input" />
              </div>
              <div>
                <Label>Öncelik</Label>
                <Select value={bulkTaskForm.priority} onValueChange={(v) => setBulkTaskForm({ ...bulkTaskForm, priority: v })}>
                  <SelectTrigger data-testid="bulk-task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITIES.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} /> {p.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Personel Seçimi ({bulkTaskForm.personnel_ids.length} seçili)</Label>
                <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setBulkTaskForm(f => ({ ...f, personnel_ids: f.personnel_ids.length === personnelList.filter(p => p.active !== false).length ? [] : personnelList.filter(p => p.active !== false).map(p => p.id) }))} data-testid="bulk-select-all-btn">
                  Tümünü Seç / Kaldır
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto border rounded-lg p-2" data-testid="bulk-personnel-list">
                {personnelList.filter(p => p.active !== false).map(p => {
                  const checked = bulkTaskForm.personnel_ids.includes(p.id);
                  return (
                    <button
                      key={p.id} type="button"
                      onClick={() => toggleBulkPerson(p.id)}
                      className={`flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded border transition-all ${checked ? 'bg-indigo-50 border-indigo-400' : 'bg-white border-slate-200 hover:border-indigo-200'}`}
                      data-testid={`bulk-person-${p.id}`}
                    >
                      <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${checked ? 'bg-indigo-600 text-white' : 'border border-slate-300'}`}>{checked && '✓'}</span>
                      <span className="truncate">{p.name}{!p.phone && <span className="text-[10px] text-amber-500 ml-1">(tel yok)</span>}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-[11px] text-indigo-700">💬 Telefonu olan her personele WhatsApp bildirimi gönderilir.</p>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBulkTaskModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-task-submit-btn">Görevi Ata</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL: ADD PAYMENT ============ */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md" data-testid="add-payment-modal">
          <DialogHeader>
            <DialogTitle>Ödeme Ekle</DialogTitle>
            <DialogDescription>Maaş, avans veya prim kaydı oluşturun (kasaya gider olarak işlenir)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-3">
            <div>
              <Label>Ödeme Türü *</Label>
              <Select value={paymentForm.payment_type} onValueChange={(v) => setPaymentForm({...paymentForm, payment_type: v})}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_TYPES.map(pt => <SelectItem key={pt.id} value={pt.id}>{pt.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Hangi Kasadan Çıkacak? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {PAYMENT_METHODS.map(pm => (
                  <button key={pm.id} type="button" onClick={() => setPaymentForm({...paymentForm, payment_method: pm.id})}
                    className={`p-2 rounded-md border-2 text-xs font-medium transition-all ${
                      paymentForm.payment_method === pm.id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`pay-pm-${pm.id}`}
                  >
                    <div className="text-base mb-0.5">{pm.icon}</div>
                    <div>{pm.label}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tutar (₺) *</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} required data-testid="payment-amount-input" /></div>
              <div><Label>Tarih</Label><Input type="date" value={paymentForm.date} onChange={(e) => setPaymentForm({...paymentForm, date: e.target.value})} /></div>
            </div>
            <div><Label>Hangi Ay (opsiyonel)</Label><Input type="month" value={paymentForm.month} onChange={(e) => setPaymentForm({...paymentForm, month: e.target.value})} placeholder="2026-05" /></div>
            <div><Label>Not</Label><Textarea rows={2} value={paymentForm.notes} onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})} /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="payment-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL: ADD LEAVE ============ */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="max-w-md" data-testid="add-leave-modal">
          <DialogHeader>
            <DialogTitle>Yeni İzin Tanımla</DialogTitle>
            <DialogDescription>Personel için izin kaydı oluşturun</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddLeave} className="space-y-3">
            <div>
              <Label>İzin Türü *</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map(lt => <SelectItem key={lt.id} value={lt.id}>{lt.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Başlangıç *</Label><Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} required data-testid="leave-start-input" /></div>
              <div><Label>Bitiş *</Label><Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} required data-testid="leave-end-input" /></div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm">
              <p className="text-blue-700">
                Toplam <span className="font-bold">{calculateDays(leaveForm.start_date, leaveForm.end_date)}</span> gün
              </p>
            </div>
            <div><Label>Açıklama</Label><Textarea rows={2} value={leaveForm.notes} onChange={(e) => setLeaveForm({...leaveForm, notes: e.target.value})} placeholder="İzin sebebi vs..." /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowLeaveModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-[#0B172A] hover:bg-[#13233C]" data-testid="leave-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* ============ MODAL: ADD EXPENSE (Cep Harcaması) ============ */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="max-w-md" data-testid="add-expense-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <FileText className="w-5 h-5" /> Personel Harcaması Ekle
            </DialogTitle>
            <DialogDescription>
              Personel kendi cebinden harcadı, kasaya yazılmaz - sadece personele borç olarak kayıt olur. Geri ödeme yapıldığında kasadan düşülür.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddExpense} className="space-y-3">
            <div><Label>Açıklama (Ne için harcandı?) *</Label><Input value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} placeholder="Örn: Su, çay malzemeleri" required data-testid="expense-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tutar (₺) *</Label><Input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})} required data-testid="expense-amount-input" /></div>
              <div><Label>Tarih</Label><Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})} /></div>
            </div>
            <div><Label>Not (opsiyonel)</Label><Textarea rows={2} value={expenseForm.notes} onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})} /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowExpenseModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="expense-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL: REIMBURSE (Geri Öde) ============ */}
      <Dialog open={showReimburseModal} onOpenChange={setShowReimburseModal}>
        <DialogContent className="max-w-md" data-testid="reimburse-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> Personele Geri Öde
            </DialogTitle>
            <DialogDescription>
              Personelin cebinden harcadığı tutar kasadan ödenecek
            </DialogDescription>
          </DialogHeader>
          {reimburseTarget && (
            <div className="space-y-4">
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="p-3">
                  <p className="text-xs text-emerald-700 uppercase tracking-wide">Geri Ödenecek Tutar</p>
                  <p className="text-3xl font-bold text-emerald-900 mt-1">{formatTL(reimburseTarget.amount)}</p>
                  <p className="text-xs text-slate-600 mt-1">{reimburseTarget.description}</p>
                </CardContent>
              </Card>
              <div>
                <Label className="mb-2 block">Hangi Kasadan Ödenecek? *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PAYMENT_METHODS.map(pm => (
                    <button key={pm.id} type="button" onClick={() => setReimburseMethod(pm.id)}
                      className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        reimburseMethod === pm.id
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`reimburse-pm-${pm.id}`}
                    >
                      <div className="text-xl mb-1">{pm.icon}</div>
                      <div>{pm.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowReimburseModal(false)} className="flex-1">İptal</Button>
                <Button onClick={handleReimburse} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-reimburse-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Öde ve Kasaya İşle
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonnelTab;
